import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { getViewsDataApi } from '../service/multitenantDashboardApi';
import { SearchText, UATConstants } from 'src/Common/Models/UATConstants';
import { set } from 'date-fns';
import {
  isValidRelatedItem,
  removeChildActionItems,
  setChildActionItems,
} from '../../utils/HubOverviewUtility';
import { ICustomFilterQuery } from 'src/Common/RTK/interface/CustomFilter';
import { MapDisplayColumnToArtemisDisplayColumn } from 'src/Common/manageViews/utils/helpers/MapDisplayColumnToArtemisDisplayColumn';
import { UserMetadata } from 'src/Common/manageViews/interfaces/UserMetadataResponse';

const initialState = {
  viewData: [],
  selectedViewData: [
    {
      selectedViewId: '',
      selectedViewName: '',
    },
  ],
  tenantActionData: [],
  tenantDashboardDisplayColumns: [],
  tenantDashboardQueryGroups: [] as any[],
  tenantDashboardGroupsInfo: [] as any[],
  isLoading: true,
  sortOrder: 'raidid desc',
  gridSearchText: '',
  isSearch: false,
  totalCount: 0,
  currentPage: 1,
  navFromPagenate: false,
  dataMessage: 'Loading Data...',
  selectedActionId: '',
  selectedDataForSplitView: [],
  currentTenantId: '',
  tenantFiltersPayload: '',
  relatedActionData: [],
  isRelatedActionAvailable: false,
  isSplitViewData: false,
};

const updateViewData = (state: typeof initialState, action: any) => {
  const defaultView = action.payload.value.find(
    (view: { ViewName: string }) => view.ViewName === UATConstants.Views.AssignedToMe
  );
  const urlParams = new URLSearchParams(new URL(window.location.href).search);
  const viewId = urlParams.get('viewId');
  const filteredData = action?.payload.value.filter(
    (item: { QueryId: any }) => item.QueryId === viewId
  );
  if (filteredData.length > 0) {
    state.selectedViewData = [
      {
        selectedViewId: filteredData[0].QueryId,
        selectedViewName: filteredData[0].ViewName,
      },
    ];
  } else {
    // Handle the case where no data matches the filter
    state.selectedViewData = [
      {
        selectedViewId: defaultView?.QueryId,
        selectedViewName: defaultView?.ViewName,
      },
    ];
  }
  state.viewData = action.payload.value;
  state.tenantDashboardDisplayColumns = defaultView?.DisplayColumns;
};
const updateViewList = (state: typeof initialState, action: any) => {
  state.viewList = action.payload.value;
};
const applyView = (state, customFilter: ICustomFilterQuery) => {
  if (customFilter) {
    state.selectedViewData = [
      {
        selectedViewId: customFilter.id,
        selectedViewName: customFilter.name,
      },
    ];

    state.currentPage = 1;
    state.totalCount = 0;
    state.tenantDashboardDisplayColumns = MapDisplayColumnToArtemisDisplayColumn(
      customFilter?.displayColumn ?? []
    );
    state.tenantDashboardQueryGroups = customFilter?.queryGroups ?? [];
    state.tenantDashboardGroupsInfo = customFilter?.groupsInfo ?? [];
  }
};

export const manageTenantSlice = createSlice({
  name: 'manageTenantSlice',
  initialState,
  reducers: {
    applyDashboardView(state, action: PayloadAction<ICustomFilterQuery>) {
      const customFilterQuery: ICustomFilterQuery = action.payload;

      if (customFilterQuery) {
        applyView(state, customFilterQuery);
      }
    },
    applyDashboardViewNoQueries(state, action: PayloadAction<ICustomFilterQuery>) {
      state.dataMessage = SearchText.NoFiltersApplied;
      state.tenantActionData = [];
      state.isLoading = false;
    },
    applyMissingView(state, action: PayloadAction<ICustomFilterQuery>) {},
    selectedViewData(state, action: any) {
      let data = [];
      data.push({
        selectedViewId: action.payload.key,
        selectedViewName: action.payload.text,
      });
      state.selectedViewData = data;
    },
    setLoading(state, action: any) {
      state.isLoading = action.payload;
    },
    setSortOrder(state, action: any) {
      state.sortOrder = action.payload;
    },
    setGridSearchText(state, action: any) {
      state.gridSearchText = action.payload;
    },
    setIsSearch(state, action: any) {
      state.isSearch = action.payload;
    },
    setCurrentPage(state, action: any) {
      state.currentPage = action.payload;
    },
    setNavFromPaginate(state, action: any) {
      state.navFromPagenate = action.payload;
    },
    setDataMessage(state, action: any) {
      state.dataMessage = action.payload;
    },
    setTotalCount(state, action: any) {
      state.totalCount = action.payload;
    },
    setSelectedActionId(state, action: any) {
      state.selectedActionId = action.payload;
    },
    setSelectedDataForSplitView(state, action: any) {
      state.selectedDataForSplitView = action.payload;
    },
    setTenantDashboardDisplayColumns(state, action: any) {
      state.tenantDashboardDisplayColumns = action.payload;
    },
    setTenantDashboardQueryData(state, action: PayloadAction<{ queryGroups: any[]; groupsInfo: any[] }>) {
      state.tenantDashboardQueryGroups = action.payload.queryGroups ?? [];
      state.tenantDashboardGroupsInfo = action.payload.groupsInfo ?? [];
    },
    setTenantFilters(state, action: any) {
      state.tenantFiltersPayload = action.payload;
    },
    setSplitViewStateData(state, action: any) {
      state.isSplitViewData = action.payload;
    },
    setViewListToStore(state, action: any) {
      updateViewList(state, action);
    },
    setViewDataToStore(state, action: any) {
      updateViewData(state, action);
    },
    applyTenantActionDataReducer(state, action) {
      applyTenantActionData(state, action);
    },
    setDefaultTenantDataForEmail(state, action: any) {
      state.tenantActionData = action.payload;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setChildActionItems, (state, action) => {
      const index = state.tenantActionData.findIndex((item) => item.raidid === action.payload.id && !('isChildAction' in item));
      if (index !== -1) {
        action?.payload?.childActions.forEach((childAction) => {
          const clonedChild = {
            ...childAction,
            raidrelations: {
              ...childAction.raidrelations,
              raidCorrelation: [],
            },
          };

          state.tenantActionData.splice(index + 1, 0, clonedChild);
        });
      }
    });
    builder.addCase(removeChildActionItems, (state, action) => {
      state.tenantActionData = state.tenantActionData.filter((item) => {
        return (
          action.payload[UATConstants.RAIDFields.RaidId] &&
          item?.parentId !== action.payload[UATConstants.RAIDFields.RaidId]
        );
      });
      state.isLoading = false;
    });
    //Deprecating this API as we move to dynamic views
    builder.addMatcher(getViewsDataApi.endpoints.getViewData.matchFulfilled, (state, action) => {
      // updateViewData(state, action);
      // const defaultView = action.payload.find(
      //   (view: { ViewName: string }) => view.ViewName === UATConstants.Views.AssignedToMe
      // );
      // const urlParams = new URLSearchParams(new URL(window.location.href).search);
      // const viewId = urlParams.get('viewId');
      // const filteredData = action?.payload.filter(
      //   (item: { QueryId: any }) => item.QueryId === viewId
      // );
      // if (filteredData.length > 0) {
      //   // state.selectedViewData = [
      //   //   {
      //   //     selectedViewId: filteredData[0].QueryId,
      //   //     selectedViewName: filteredData[0].ViewName,
      //   //   },
      //   // ];
      // } else {
      //   // Handle the case where no data matches the filter
      //   // state.selectedViewData = [
      //   //   {
      //   //     selectedViewId: defaultView?.QueryId,
      //   //     selectedViewName: defaultView?.ViewName,
      //   //   },
      //   // ];
      // }
      // state.viewData = action.payload;
      //state.tenantDashboardDisplayColumns = defaultView?.DisplayColumns;
    });
    builder.addMatcher(
      getViewsDataApi.endpoints.getTenantActionData.matchFulfilled,
      (state, action) => {
        applyTenantActionData(state, action);
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.getTenantSearchData.matchFulfilled,
      (state, action) => {
        if (state.isSearch) {
          state.totalCount = action.payload['@odata.count'] ? action.payload['@odata.count'] : 0;
          if (action.payload.value.length == 0) {
            state.dataMessage = SearchText.NoActions;
          }
          state.tenantActionData = action.payload.value;
          state.isRelatedActionAvailable =
            Array.isArray(action.payload?.value) &&
            action.payload.value.some((obj) => obj.raidrelations?.raidCorrelation?.length > 0);

          state.isSearch = false;
        }
        state.isLoading = false;
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.getTenantRelatedData.matchFulfilled,
      (state, action) => {
        if (action.payload.value.length == 0) {
          state.dataMessage = SearchText.NoActions;
        }
        state.relatedActionData = [...state.relatedActionData, ...action.payload.value];
        state.isLoading = false;
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.getTenantBulkEdit.matchFulfilled,
      (state, action) => {
        /* if (action.payload.value.length == 0) {
          state.dataMessage = SearchText.NoActions;
        } */
        /* const updatedStateData = state.tenantActionData.forEach((item) => {
          action.payload.forEach((element) => {
            if (element.raidid === item.raidid) {
              item.fields = element.fields;
            }
          });
          return item;
        }); */
        const getRaidIds = action.payload?.map((item) => item?.raidid);
        const updatedStateData = state?.tenantActionData?.map((item) => {
          if (getRaidIds?.includes(item.raidid)) {
            const actionIndex = action.payload?.findIndex(
              (element) => element.raidid === item.raidid
            );
            return { ...item, ...action.payload[actionIndex] };
          }
          return item;
        });
        state.tenantActionData = updatedStateData;
        state.isLoading = false;
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.getTenantBulkDelete.matchFulfilled,
      (state, action) => {
        const getRaidIds = action.payload?.results.map((item) => item?.raidId);
        const updatedStateData = state?.tenantActionData?.filter(
          (item) => !getRaidIds?.includes(item.raidid)
        );
        state.tenantActionData = updatedStateData;
        state.isLoading = false;
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.createUserMetadata.matchFulfilled,
      (state, action) => {
        const userMetaData: UserMetadata = action.payload;

        if (userMetaData?.DefaultView?.Id && userMetaData?.DefaultView?.Name) {
          state.selectedViewData = [
            {
              selectedViewId: userMetaData.id,
              selectedViewName: userMetaData.name,
            },
          ];
        }
      }
    );
    builder.addMatcher(getViewsDataApi.endpoints.deleteView.matchFulfilled, (state, action) => {
      const deletedViewArgs = action.meta.arg.originalArgs as { id: string; tenantId: string };
      const deletedViewId: string = deletedViewArgs.id;
      const tenantId: string = deletedViewArgs.tenantId;

      const currentSelectedViewId: string = state.selectedViewData[0]?.selectedViewId;

      if (currentSelectedViewId === deletedViewId) {
        state.selectedViewData = [
          {
            selectedViewId: '',
            selectedViewName: '',
          },
        ];
      }
    });
    builder.addMatcher(getViewsDataApi.endpoints.getAMMDashboardTaskList.matchFulfilled, (state, action) => {
      state.tenantTaskData = action.payload;
    });
  },
});

export const {
  applyDashboardView,
  applyDashboardViewNoQueries,
  selectedViewData,
  setLoading,
  setSortOrder,
  setGridSearchText,
  setIsSearch,
  setCurrentPage,
  setNavFromPaginate,
  setDataMessage,
  setTotalCount,
  setSelectedActionId,
  setSelectedDataForSplitView,
  setTenantDashboardDisplayColumns,
  setTenantDashboardQueryData,
  setTenantFilters,
  setSplitViewStateData,
  setViewDataToStore,
  applyTenantActionDataReducer,
  setDefaultTenantDataForEmail,
  setViewListToStore,
} = manageTenantSlice.actions;

// Generated by GitHub Copilot | Updated on: 2026-06-26 | 8 lines modified | Skipped deleted raidids in tenantActionData

export const applyTenantActionData = (state, action) => {
  // Check for specific response status codes and set the data message accordingly
  if (
    action?.meta?.baseQueryMeta?.response?.status === 203 ||
    action?.meta?.baseQueryMeta?.response?.status === 500
  ) {
    state.dataMessage = action.payload?.message || 'An error occurred';
  }

  // Check if the payload value is an empty array and set the data message
  if (action.payload?.value && action.payload.value.length === 0) {
    state.dataMessage = action.payload?.message || SearchText.NoRequests;
  }

  // Set the total count from the payload or default to 0
  state.totalCount = action.payload['@odata.count'] || 0;

  // Set the tenant action data from the payload or default to an empty array
  state.tenantActionData = action.payload?.value || [];

  // Set the loading state to false
  state.isLoading = false;

  // Set the selected action ID from the payload or default to an empty string
  state.selectedActionId = action.payload?.value?.[0]?.raidid || '';

  // Set the selected data for split view from the payload or default to an empty array
  state.selectedDataForSplitView =
    action.payload?.value?.length > 0 ? [action.payload.value[0]] : [];
  state.isRelatedActionAvailable =
    Array.isArray(action?.payload?.value) &&
    action.payload.value.some(
      (obj) =>
        obj.raidrelations?.raidCorrelation?.length > 0 &&
        obj.raidrelations.raidCorrelation.some((relation) => isValidRelatedItem(relation))
    );
};
// Generated by GitHub Copilot | Updated on: 2026-04-06 | 8 lines modified | Added tenantDashboardQueryGroups/GroupsInfo state and setTenantDashboardQueryData reducer to fix stale export data after tenant switch